import { BASE_CONTRACT_FIXTURE_IDS, CONTRACT_FIXTURE_IDS, ContractFixtureId } from "./contracts/contract-fixtures";

/**
 * The dev-only offline mock scenario. Deliberately free of Angular imports: it is read by the
 * HTTP interceptor and written by the dev panel, and neither needs a service for it.
 *
 * Mirrors src/environments/offline-mode-override.ts — pure functions, try/catch around
 * localStorage, no-op when storage is unavailable.
 */
export const MOCK_SCENARIO_STORAGE_KEY = "dev.mockScenario";

export const PRESET_IDS = [
  "full-portfolio",
  "employer-only",
  "own-savings-only",
  "new-saver",
  "no-contracts",
  "partial-error",
] as const;

export type PresetId = (typeof PRESET_IDS)[number];

export interface MockScenario {
  readonly preset: PresetId;
  readonly contracts: readonly ContractFixtureId[];
  /** The Avtaler partial-error banner. Unconditionally on before this work. */
  readonly partialError: boolean;
  readonly externalContracts: boolean;
  readonly navContracts: boolean;
  /** false makes the consent alert reachable. */
  readonly hasAllConsents: boolean;
}

export const DEFAULT_PRESET_ID: PresetId = "full-portfolio";

// Base fixtures only: the extended ones are for the AI mock chat and must not change the default
// offline data or the e2e expectations.
const ALL_CONTRACTS: readonly ContractFixtureId[] = BASE_CONTRACT_FIXTURE_IDS;
const EMPLOYER_CONTRACTS: readonly ContractFixtureId[] = ["epk-1", "epk-moved", "otp-1", "kron-1"];
const USER_SAVINGS_CONTRACTS: readonly ContractFixtureId[] = ["fond-1", "ips-1", "ask-1", "spar-1"];

export const MOCK_SCENARIO_PRESETS: Readonly<Record<PresetId, MockScenario>> = {
  "full-portfolio": {
    preset: "full-portfolio",
    contracts: ALL_CONTRACTS,
    partialError: false,
    externalContracts: true,
    navContracts: true,
    hasAllConsents: true,
  },
  "employer-only": {
    preset: "employer-only",
    contracts: EMPLOYER_CONTRACTS,
    partialError: false,
    externalContracts: true,
    navContracts: false,
    hasAllConsents: true,
  },
  "own-savings-only": {
    preset: "own-savings-only",
    contracts: USER_SAVINGS_CONTRACTS,
    partialError: false,
    externalContracts: false,
    navContracts: false,
    hasAllConsents: true,
  },
  "new-saver": {
    preset: "new-saver",
    contracts: ["spar-1"],
    partialError: false,
    externalContracts: false,
    navContracts: false,
    hasAllConsents: false,
  },
  "no-contracts": {
    preset: "no-contracts",
    contracts: [],
    partialError: false,
    externalContracts: false,
    navContracts: false,
    hasAllConsents: true,
  },
  "partial-error": {
    preset: "partial-error",
    contracts: ALL_CONTRACTS,
    partialError: true,
    externalContracts: true,
    navContracts: true,
    hasAllConsents: true,
  },
};

export const PRESET_LABELS: Readonly<Record<PresetId, string>> = {
  "full-portfolio": "Full portfolio",
  "employer-only": "Employer only",
  "own-savings-only": "Own savings only",
  "new-saver": "New saver",
  "no-contracts": "No contracts",
  "partial-error": "Partial error",
};

function isPresetId(value: unknown): value is PresetId {
  return typeof value === "string" && (PRESET_IDS as readonly string[]).includes(value);
}

function isFixtureId(value: unknown): value is ContractFixtureId {
  return typeof value === "string" && (CONTRACT_FIXTURE_IDS as readonly string[]).includes(value);
}

export function getPreset(id: string): MockScenario {
  return isPresetId(id) ? MOCK_SCENARIO_PRESETS[id] : MOCK_SCENARIO_PRESETS[DEFAULT_PRESET_ID];
}

/**
 * Parses a stored value. Unknown fixture ids are dropped so a scenario written before a fixture
 * rename cannot break the app; anything structurally wrong falls back to the default preset.
 */
function parseMockScenario(raw: string | null): MockScenario {
  if (raw === null) {
    return MOCK_SCENARIO_PRESETS[DEFAULT_PRESET_ID];
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (typeof parsed !== "object" || parsed === null) {
      return MOCK_SCENARIO_PRESETS[DEFAULT_PRESET_ID];
    }

    const candidate = parsed as Record<string, unknown>;

    if (
      !isPresetId(candidate["preset"]) ||
      !Array.isArray(candidate["contracts"]) ||
      typeof candidate["partialError"] !== "boolean" ||
      typeof candidate["externalContracts"] !== "boolean" ||
      typeof candidate["navContracts"] !== "boolean" ||
      typeof candidate["hasAllConsents"] !== "boolean"
    ) {
      return MOCK_SCENARIO_PRESETS[DEFAULT_PRESET_ID];
    }

    return {
      preset: candidate["preset"],
      contracts: [...new Set(candidate["contracts"].filter(isFixtureId))],
      partialError: candidate["partialError"],
      externalContracts: candidate["externalContracts"],
      navContracts: candidate["navContracts"],
      hasAllConsents: candidate["hasAllConsents"],
    };
  } catch {
    return MOCK_SCENARIO_PRESETS[DEFAULT_PRESET_ID];
  }
}

export function readMockScenario(): MockScenario {
  try {
    return parseMockScenario(globalThis.localStorage?.getItem(MOCK_SCENARIO_STORAGE_KEY) ?? null);
  } catch {
    return MOCK_SCENARIO_PRESETS[DEFAULT_PRESET_ID];
  }
}

export function writeMockScenario(scenario: MockScenario): void {
  try {
    globalThis.localStorage?.setItem(MOCK_SCENARIO_STORAGE_KEY, JSON.stringify(scenario));
  } catch {
    // Storage unavailable (private mode, blocked cookies) — the scenario simply does not persist.
  }
}

export function clearMockScenario(): void {
  try {
    globalThis.localStorage?.removeItem(MOCK_SCENARIO_STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}
